import { Flex, Space, Spacing } from '@billy/design-system'
import isNil from 'lodash/isNil'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'

import { useActiveBookkeepingBankAccount } from '@features/accounts/hooks/useActiveBookkeepingBankAccount'
import { useDefaultBookkeepingBankAccount } from '@features/accounts/hooks/useDefaultBookkeepingBankAccount'
import { BankAccountSelect } from '@features/bankAccounts/elements/BankAccountSelect'
import { BankAccountSource } from '@features/bankAccounts/enums/bankAccountSource'
import { useOpenBankingAccounts } from '@features/openBanking/hooks/useOpenBankingAccounts'
import { isOpenBankingAccountDeleted } from '@features/openBanking/utils/isOpenBankingAccountDeleted'

import { useSandboxModeContext } from '../../contexts'
import { SANDBOX_BOOKKEEPING_ACCOUNT_ID } from '../../contexts/sandbox/sandboxSeedData'
import { useSandboxBankAccountByBookkeepingAccountId } from '../../hooks/useSandboxBankAccountByBookkeepingAccountId'
import { useSandboxBankTransactionsFilters } from '../../hooks/useSandboxBankTransactionsFilters'
import { BankTransactionsAccountBalance } from './elements/BankTransactionsAccountBalance'
import { BankTransactionsDateSelector } from './elements/BankTransactionsDateSelector/BankTransactionsDateSelector'
import { BankTransactionsSearch } from './elements/BankTransactionsSearch/BankTransactionsSearch'
import { BankTransactionsSorting } from './elements/BankTransactionsSorting/BankTransactionsSorting'
import { BankTransactionsStatusFilter } from './elements/BankTransactionsStatusFilter/BankTransactionsStatusFilter'
import * as Styled from './styles'

export const BankTransactionsFilters = (): ReactElement => {
  const sandbox = useSandboxModeContext()
  const [filters, setFilters] = useSandboxBankTransactionsFilters()
  const { accountTotalBalance, balanceCurrency, source } = useSandboxBankAccountByBookkeepingAccountId(
    filters.accountId,
  )
  const { defaultBookkeepingBankAccount } = useDefaultBookkeepingBankAccount()
  const { activeBookkeepingBankAccount } = useActiveBookkeepingBankAccount()
  const { openBankingAccounts } = useOpenBankingAccounts({
    enabled: !sandbox,
    withDeleted: true,
  })

  const openBankingAccount = useMemo(
    () => openBankingAccounts?.find((account) => account.externalAccountId === filters.accountId),
    [openBankingAccounts, filters.accountId],
  )

  const isConnectionDeleted = openBankingAccount ? isOpenBankingAccountDeleted(openBankingAccount) : false
  const shouldShowTotalBalance = !isNil(source) && source !== BankAccountSource.BILLY && !isConnectionDeleted
  const accountId = sandbox
    ? SANDBOX_BOOKKEEPING_ACCOUNT_ID
    : defaultBookkeepingBankAccount?.id || activeBookkeepingBankAccount?.id

  useEffect(() => {
    if (accountId && !filters.accountId) {
      setFilters({ accountId })
    }
  }, [accountId, filters.accountId, setFilters])

  const handleAccountSelect = useCallback(
    (id?: string) => {
      if (id) {
        setFilters({ accountId: id })
      }
    },
    [setFilters],
  )

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Styled.AccountSelectWrapper>
          <BankAccountSelect
            accounts={sandbox?.sandboxState.accounts}
            bankAccounts={sandbox?.sandboxState.bankAccounts}
            selectedId={filters.accountId}
            onSelect={handleAccountSelect}
          />
        </Styled.AccountSelectWrapper>
        <BankTransactionsDateSelector />
      </Flex>
      <Space size="s32" />
      {shouldShowTotalBalance && (
        <>
          <BankTransactionsAccountBalance amount={accountTotalBalance} currency={balanceCurrency} />
          <Space size="s32" />
        </>
      )}
      <Flex justifyContent="space-between" alignItems="center" gap={Spacing.S40}>
        <BankTransactionsSearch />
        <BankTransactionsSorting />
      </Flex>
      <Space size="s16" />
      <BankTransactionsStatusFilter />
      <Space size="s40" />
    </>
  )
}
